import React from "react";

/**
 * The allergy banner (build plan §7.1 #3): on EVERY person screen, at the top,
 * before anything else about the person. Medication allergies are named
 * separately (Decisions 2 #22).
 */

export interface AllergyBannerData {
  state: "listed" | "none" | "unknown";
  allergies: { allergy: string; reaction: string; category: string; anaphylaxisRisk: "Yes" | "No" | null; bringingEpiPen: "Yes" | "No" | null }[];
  medicationAllergy: string | null;
}

export function AllergyBanner({ data }: { data: AllergyBannerData }): React.ReactElement {
  if (data.state === "none") {
    return (
      <div className="mb-4 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700" role="note" aria-label="Allergies">
        <span className="font-semibold">Allergies:</span> none listed by the family.
      </div>
    );
  }
  if (data.state === "unknown") {
    return (
      <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-950" role="note" aria-label="Allergies">
        <span className="font-semibold">Allergies:</span> not answered in the application. Confirm with the family.
      </div>
    );
  }
  const severe = data.allergies.some((a) => a.anaphylaxisRisk === "Yes");
  return (
    <div className="mb-4 rounded-md border-2 border-red-600 bg-red-50 px-4 py-3 text-sm text-red-950" role="alert" aria-label="Allergies">
      <p className="font-bold uppercase tracking-wide">
        Allergies{severe ? " — anaphylaxis risk" : ""}
      </p>
      <ul className="mt-1 space-y-0.5">
        {data.allergies.map((a, i) => (
          <li key={i}>
            <span className="font-semibold">{a.allergy}</span>
            {a.category ? ` (${a.category.toLowerCase()})` : ""}
            {a.reaction ? ` — ${a.reaction}` : ""}
            {a.anaphylaxisRisk === "Yes" ? " · ANAPHYLAXIS RISK" : ""}
            {a.bringingEpiPen === "Yes" ? " · bringing an epinephrine auto-injector" : ""}
          </li>
        ))}
        {data.medicationAllergy && (
          <li>
            <span className="font-semibold">Medication allergy:</span> {data.medicationAllergy}
          </li>
        )}
      </ul>
    </div>
  );
}
